/**
 * RatioDashboard — flagship director view. Calm green/amber/red signals
 * for live child-to-staff ratios per room. NO alarmist language.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Users, Briefcase, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const STATUS_PALETTE = {
  green: "bg-[#E6EFE8] text-[#1F3A2A] border-[#1F3A2A]/30",
  amber: "bg-[#FDF1D2] text-[#8C6D1F] border-[#F4E5C5]",
  red: "bg-[#FCEEEE] text-[#A11D1D] border-[#FCD3D3]",
};

function StatusPill({ status, t }) {
  return (
    <span
      data-testid={`ratio-status-${status}`}
      className={`inline-block text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2.5 py-0.5 border ${STATUS_PALETTE[status]}`}
    >
      {t(`ops.ratio.status_${status}`)}
    </span>
  );
}

function KpiTile({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#EEF0F4] bg-white p-5 flex items-start gap-3"
    >
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
        style={{ background: accent + "22", color: accent }}
      >
        <Icon size={18} strokeWidth={1.6} />
      </span>
      <div>
        <div className="eyebrow">{label}</div>
        <div className="mt-1 font-display text-2xl text-[#1A1B2E] tabular-nums">{value}</div>
      </div>
    </div>
  );
}

export default function RatioDashboard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/ops/ratio-dashboard");
      setData(r.data);
    } catch (e) {
      // calm fall-through
    }
    setLoading(false);
    setRefreshing(false);
  };
  useEffect(() => {
    load();
    const t1 = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t1);
  }, []);

  if (loading) {
    return (
      <main className="p-6 lg:p-10">
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      </main>
    );
  }
  if (!data) return null;
  const overall = data.overall_status;

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-ratio-dashboard">
      <AdminHeader
        eyebrow={t("ops.ratio.eyebrow")}
        title={
          <>
            {t("ops.ratio.title_lead")}{" "}
            <em className="font-serif italic text-[#1F3A2A]">
              {t("ops.ratio.title_italic")}
            </em>
          </>
        }
        action={
          <button
            onClick={load}
            data-testid="ratio-refresh"
            className={`inline-flex items-center gap-2 rounded-full bg-white text-[#1F3A2A] border border-[#EEF0F4] text-sm font-medium px-4 py-2 hover:bg-[#FAFAFB] ${refreshing ? "opacity-60" : ""}`}
          >
            <RefreshCw size={14} strokeWidth={1.8} />
            Refresh
          </button>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("ops.ratio.blurb")}</p>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          icon={Users}
          label={t("ops.ratio.kpi_children")}
          value={data.total_children_present}
          accent="#1F3A2A"
          testid="ratio-kpi-children"
        />
        <KpiTile
          icon={Briefcase}
          label={t("ops.ratio.kpi_staff")}
          value={data.total_staff_present}
          accent="#3D5C46"
          testid="ratio-kpi-staff"
        />
        <KpiTile
          icon={AlertTriangle}
          label={t("ops.ratio.kpi_risk")}
          value={data.risk_room_count}
          accent={data.risk_room_count > 0 ? "#A11D1D" : "#1F8A3F"}
          testid="ratio-kpi-risk"
        />
        <KpiTile
          icon={CheckCircle2}
          label={t("ops.ratio.kpi_overall")}
          value={
            <StatusPill status={overall} t={t} />
          }
          accent={overall === "red" ? "#A11D1D" : overall === "amber" ? "#8C6D1F" : "#1F8A3F"}
          testid="ratio-kpi-overall"
        />
      </section>

      <section className="rounded-3xl bg-white border border-[#EEF0F4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-[#6B7280] uppercase tracking-[0.1em] text-[10px] bg-[#FAFAFB]">
              <tr>
                <th className="px-5 py-3">{t("ops.ratio.table_room")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_age_band")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_children")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_staff")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_current")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_required")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_occupancy")}</th>
                <th className="px-5 py-3">{t("ops.ratio.table_status")}</th>
              </tr>
            </thead>
            <tbody>
              {(data.rooms || []).length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center font-serif italic text-[#9CA3AF]">
                    {t("ops.ratio.no_rooms")}
                  </td>
                </tr>
              ) : (
                data.rooms.map((r) => (
                  <tr key={r.room_id || r.room_name} className="border-t border-[#EEF0F4]" data-testid={`ratio-room-${r.room_id || r.room_name}`}>
                    <td className="px-5 py-3 font-medium text-[#1A1B2E]">{r.room_name}</td>
                    <td className="px-5 py-3 text-[#6B7280]">{r.age_band || "—"}</td>
                    <td className="px-5 py-3 tabular-nums">{r.children_present}</td>
                    <td className="px-5 py-3 tabular-nums">{r.staff_present}</td>
                    <td className="px-5 py-3 tabular-nums">{r.current_ratio_text}</td>
                    <td className="px-5 py-3 tabular-nums text-[#6B7280]">{r.required_ratio_text}</td>
                    <td className="px-5 py-3 tabular-nums text-[#6B7280]">
                      {r.occupancy_percent != null ? `${r.occupancy_percent}%` : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <StatusPill status={r.status} t={t} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-[#9CA3AF]">
        {t("ops.ratio.generated_at")}: {data.generated_at?.slice(11, 16)} UTC
      </p>
    </main>
  );
}
